import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, User, Car, FileSignature, FileText, 
  Download, Eye, CheckCircle, AlertTriangle, Trash2, Send, Loader2, Home
} from 'lucide-react';

const HandoverForm = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [handover, setHandover] = useState(null);
  const [isNewHandover, setIsNewHandover] = useState(true);
  
  // Customer canvas refs
  const customerCanvasRef = useRef(null);
  const customerCtxRef = useRef(null);
  const [isCustomerDrawing, setIsCustomerDrawing] = useState(false);
  const [customerSigned, setCustomerSigned] = useState(false);
  
  // Representative canvas refs
  const repCanvasRef = useRef(null);
  const repCtxRef = useRef(null);
  const [isRepDrawing, setIsRepDrawing] = useState(false);
  const [repSigned, setRepSigned] = useState(false);
  
  const [formData, setFormData] = useState({
    // Customer fields
    customer_first_name: '',
    customer_surname: '',
    customer_address: '',
    customer_postcode: '',
    customer_telephone: '',
    customer_email: '',
    customer_dvla_check_code: '',
    customer_bank_account_number: '',
    customer_sort_code: '',
    customer_account_holder_name: '',
    customer_additional_notes: '',
    // Booking fields
    collection_date: '',
    collection_time: '',
    return_date: '',
    return_time: '',
    total_cost: 0,
    amount_received: 0,
    security_deposit_received: false,
    // Vehicle fields
    vehicle_mileage: '',
    vehicle_condition_notes: '',
    // Signatures
    customer_signature: null,
    customer_signature_date: null,
    representative_signature: null,
    representative_signature_date: null,
    representative_name: '',
  });
  
  const [customerDocuments, setCustomerDocuments] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Initialize canvas contexts
  const initCanvas = useCallback((canvasRef, ctxRef) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1f1a';
    ctx.lineWidth = 2;
    ctxRef.current = ctx;
  }, []);

  useEffect(() => {
    // Initialize both canvases after component mounts
    const timer = setTimeout(() => {
      initCanvas(customerCanvasRef, customerCtxRef);
      initCanvas(repCanvasRef, repCtxRef);
    }, 100);
    return () => clearTimeout(timer);
  }, [initCanvas, loading]);

  // Re-initialize canvas when signature is cleared
  useEffect(() => {
    if (!customerSigned) {
      const timer = setTimeout(() => {
        initCanvas(customerCanvasRef, customerCtxRef);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [customerSigned, initCanvas]);

  useEffect(() => {
    if (!repSigned) {
      const timer = setTimeout(() => {
        initCanvas(repCanvasRef, repCtxRef);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [repSigned, initCanvas]);

  // Fetch existing handover or booking data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check if handover exists
        const handoverRes = await fetch(
          `${API_URL}/api/handovers/booking/${bookingId}`,
          { credentials: 'include' }
        );
        
        if (handoverRes.ok) {
          const existingHandover = await handoverRes.json();
          if (existingHandover) {
            setHandover(existingHandover);
            setIsNewHandover(false);
            setFormData({
              customer_first_name: existingHandover.customer_first_name || '',
              customer_surname: existingHandover.customer_surname || '',
              customer_address: existingHandover.customer_address || '',
              customer_postcode: existingHandover.customer_postcode || '',
              customer_telephone: existingHandover.customer_telephone || '',
              customer_email: existingHandover.customer_email || '',
              customer_dvla_check_code: existingHandover.customer_dvla_check_code || '',
              customer_bank_account_number: existingHandover.customer_bank_account_number || '',
              customer_sort_code: existingHandover.customer_sort_code || '',
              customer_account_holder_name: existingHandover.customer_account_holder_name || '',
              customer_additional_notes: existingHandover.customer_additional_notes || '',
              collection_date: existingHandover.collection_date || '',
              collection_time: existingHandover.collection_time || '',
              return_date: existingHandover.return_date || '',
              return_time: existingHandover.return_time || '',
              total_cost: existingHandover.total_cost || 0,
              amount_received: existingHandover.amount_received || 0,
              security_deposit_received: existingHandover.security_deposit_received || false,
              vehicle_mileage: existingHandover.vehicle_mileage || '',
              vehicle_condition_notes: existingHandover.vehicle_condition_notes || '',
              customer_signature: existingHandover.customer_signature,
              customer_signature_date: existingHandover.customer_signature_date,
              representative_signature: existingHandover.representative_signature,
              representative_signature_date: existingHandover.representative_signature_date,
              representative_name: existingHandover.representative_name || '',
            });
            setCustomerDocuments(existingHandover.customer_documents || {});
            setCustomerSigned(!!existingHandover.customer_signature);
            setRepSigned(!!existingHandover.representative_signature);
            setLoading(false);
            return;
          }
        }
        
        // No existing handover, fetch booking and customer data
        const bookingRes = await fetch(
          `${API_URL}/api/bookings/${bookingId}`,
          { credentials: 'include' }
        );
        
        if (!bookingRes.ok) {
          throw new Error('Booking not found');
        }
        
        const booking = await bookingRes.json();
        
        // Fetch customer
        const customerRes = await fetch(
          `${API_URL}/api/customers/${booking.customer_id}`,
          { credentials: 'include' }
        );
        
        if (!customerRes.ok) {
          throw new Error('Customer not found');
        }
        
        const customer = await customerRes.json();
        
        // Fetch last condition notes
        const conditionRes = await fetch(
          `${API_URL}/api/handovers/last-condition-notes`,
          { credentials: 'include' }
        );
        
        let lastConditionNotes = '';
        if (conditionRes.ok) {
          const data = await conditionRes.json();
          lastConditionNotes = data.vehicle_condition_notes || '';
        }
        
        setFormData({
          customer_first_name: customer.first_name || '',
          customer_surname: customer.surname || '',
          customer_address: customer.address || '',
          customer_postcode: customer.postcode || '',
          customer_telephone: customer.telephone || '',
          customer_email: customer.email || '',
          customer_dvla_check_code: customer.dvla_check_code || '',
          customer_bank_account_number: customer.bank_account_number || '',
          customer_sort_code: customer.sort_code || '',
          customer_account_holder_name: customer.account_holder_name || '',
          customer_additional_notes: customer.additional_notes || '',
          collection_date: booking.collection_date || '',
          collection_time: booking.collection_time || '',
          return_date: booking.return_date || '',
          return_time: booking.return_time || '',
          total_cost: booking.total_cost || 0,
          amount_received: booking.amount_received || 0,
          security_deposit_received: booking.security_deposit_received || false,
          vehicle_mileage: '',
          vehicle_condition_notes: lastConditionNotes,
          customer_signature: null,
          customer_signature_date: null,
          representative_signature: null,
          representative_signature_date: null,
          representative_name: '',
        });
        setCustomerDocuments(customer.documents || {});
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [bookingId, API_URL]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  // Drawing functions for signature pads
  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e, canvasRef, ctxRef, setIsDrawing) => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    
    const { x, y } = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    e.preventDefault();
  };

  const draw = (e, canvasRef, ctxRef, isDrawing) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    
    const { x, y } = getCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
    e.preventDefault();
  };

  const stopDrawing = (ctxRef, setIsDrawing) => {
    const ctx = ctxRef.current;
    if (ctx) {
      ctx.closePath();
    }
    setIsDrawing(false);
  };

  const clearSignature = (canvasRef, ctxRef, setSignedState, signatureField, dateField) => {
    // Reset state first - this will cause canvas to re-render
    setSignedState(false);
    setFormData(prev => ({
      ...prev,
      [signatureField]: null,
      [dateField]: null
    }));
    
    // Clear canvas context ref so it gets re-initialized on next draw
    ctxRef.current = null;
    
    // If canvas exists (signature wasn't confirmed yet), clear it
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const confirmSignature = (canvasRef, setSignedState, signatureField, dateField) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Create a new canvas with white background for JPEG export
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');
    
    // Fill with white background
    tempCtx.fillStyle = '#FFFFFF';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw the signature on top
    tempCtx.drawImage(canvas, 0, 0);
    
    // Use PNG for better quality (black lines on white background)
    const signatureData = tempCanvas.toDataURL('image/png');
    const signatureDate = new Date().toISOString();
    
    setFormData(prev => ({
      ...prev,
      [signatureField]: signatureData,
      [dateField]: signatureDate
    }));
    setSignedState(true);
    toast.success('Signature captured');
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      let response;
      
      if (isNewHandover) {
        // Create new handover - send ALL form data including customer/booking updates
        const payload = {
          booking_id: bookingId,
          // Customer fields - will update customer record if changed
          customer_first_name: formData.customer_first_name,
          customer_surname: formData.customer_surname,
          customer_address: formData.customer_address,
          customer_postcode: formData.customer_postcode,
          customer_telephone: formData.customer_telephone,
          customer_email: formData.customer_email,
          customer_dvla_check_code: formData.customer_dvla_check_code,
          customer_bank_account_number: formData.customer_bank_account_number,
          customer_sort_code: formData.customer_sort_code,
          customer_account_holder_name: formData.customer_account_holder_name,
          customer_additional_notes: formData.customer_additional_notes,
          // Booking fields - will update booking record if changed
          collection_date: formData.collection_date,
          collection_time: formData.collection_time,
          return_date: formData.return_date,
          return_time: formData.return_time,
          total_cost: formData.total_cost,
          amount_received: formData.amount_received,
          security_deposit_received: formData.security_deposit_received,
          // Vehicle info
          vehicle_mileage: formData.vehicle_mileage ? parseInt(formData.vehicle_mileage) : null,
          vehicle_condition_notes: formData.vehicle_condition_notes,
          // Signatures
          customer_signature: formData.customer_signature,
          customer_signature_date: formData.customer_signature_date,
          representative_signature: formData.representative_signature,
          representative_signature_date: formData.representative_signature_date,
          representative_name: formData.representative_name,
        };
        
        const payloadStr = JSON.stringify(payload);
        const payloadSize = payloadStr.length;
        console.log('Saving handover, payload size:', payloadSize, 'bytes');
        
        // Check if payload is too large (>900KB to be safe with 1MB limit)
        if (payloadSize > 900000) {
          throw new Error('Form data is too large. Please try with smaller signatures.');
        }
        
        response = await fetch(`${API_URL}/api/handovers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: payloadStr
        });
      } else {
        // Update existing handover
        console.log('Updating handover:', handover.handover_id);
        const payloadStr = JSON.stringify(formData);
        console.log('Update payload size:', payloadStr.length, 'bytes');
        
        response = await fetch(`${API_URL}/api/handovers/${handover.handover_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: payloadStr
        });
      }
      
      console.log('Response status:', response.status, response.statusText);
      
      // Handle response - try multiple methods to read body
      let responseText = '';
      let result;
      
      try {
        // Try to get response as ArrayBuffer first (can't be intercepted as easily)
        const buffer = await response.arrayBuffer();
        responseText = new TextDecoder().decode(buffer);
      } catch (bufferError) {
        console.warn('ArrayBuffer read failed:', bufferError);
        // Fallback: create a fresh request if response was consumed
        try {
          const retryResponse = await fetch(response.url, {
            method: response.ok ? 'GET' : 'HEAD',
            credentials: 'include'
          });
          responseText = await retryResponse.text();
        } catch (retryError) {
          // Last resort: assume success if status was ok
          if (response.ok) {
            responseText = '{"success": true}';
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        }
      }
      
      console.log('Response text length:', responseText.length);
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', responseText.substring(0, 500));
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
          throw new Error('Server error - received HTML instead of JSON. Please try again.');
        }
        throw new Error(`Server returned invalid response (status ${response.status})`);
      }
      
      if (!response.ok) {
        throw new Error(result.detail || `Failed to save handover (status ${response.status})`);
      }
      
      setHandover(result);
      setIsNewHandover(false);
      toast.success('Handover saved successfully');
      
    } catch (error) {
      console.error('Error saving handover:', error);
      toast.error(error.message || 'Failed to save handover');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendHandover = async () => {
    if (!formData.customer_email) {
      toast.error('Customer has no email address');
      return;
    }
    
    setSending(true);
    
    try {
      // First, save the handover to ensure all data is up to date
      let handoverId = handover?.handover_id;
      
      if (isNewHandover || !handoverId) {
        // Create new handover first
        const saveResponse = await fetch(`${API_URL}/api/handovers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            booking_id: bookingId,
            customer_first_name: formData.customer_first_name,
            customer_surname: formData.customer_surname,
            customer_address: formData.customer_address,
            customer_postcode: formData.customer_postcode,
            customer_telephone: formData.customer_telephone,
            customer_email: formData.customer_email,
            customer_dvla_check_code: formData.customer_dvla_check_code,
            customer_bank_account_number: formData.customer_bank_account_number,
            customer_sort_code: formData.customer_sort_code,
            customer_account_holder_name: formData.customer_account_holder_name,
            customer_additional_notes: formData.customer_additional_notes,
            collection_date: formData.collection_date,
            collection_time: formData.collection_time,
            return_date: formData.return_date,
            return_time: formData.return_time,
            total_cost: formData.total_cost,
            amount_received: formData.amount_received,
            security_deposit_received: formData.security_deposit_received,
            vehicle_mileage: formData.vehicle_mileage ? parseInt(formData.vehicle_mileage) : null,
            vehicle_condition_notes: formData.vehicle_condition_notes,
            customer_signature: formData.customer_signature,
            customer_signature_date: formData.customer_signature_date,
            representative_signature: formData.representative_signature,
            representative_signature_date: formData.representative_signature_date,
            representative_name: formData.representative_name,
          })
        });
        
        const saveClone = saveResponse.clone();
        let saveText;
        try {
          saveText = await saveClone.text();
        } catch (e) {
          saveText = await saveResponse.text();
        }
        
        let saveResult;
        try {
          saveResult = JSON.parse(saveText);
        } catch (parseError) {
          throw new Error('Failed to save handover');
        }
        
        if (!saveResponse.ok) {
          throw new Error(saveResult.detail || 'Failed to save handover');
        }
        
        setHandover(saveResult);
        setIsNewHandover(false);
        handoverId = saveResult.handover_id;
        toast.success('Handover saved');
      } else {
        // Update existing handover
        const saveResponse = await fetch(`${API_URL}/api/handovers/${handoverId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
        
        const saveClone = saveResponse.clone();
        let saveText;
        try {
          saveText = await saveClone.text();
        } catch (e) {
          saveText = await saveResponse.text();
        }
        
        let saveResult;
        try {
          saveResult = JSON.parse(saveText);
        } catch (parseError) {
          throw new Error('Failed to save handover');
        }
        
        if (!saveResponse.ok) {
          throw new Error(saveResult.detail || 'Failed to save handover');
        }
        
        setHandover(saveResult);
        toast.success('Handover saved');
      }
      
      // Now send the email
      const response = await fetch(`${API_URL}/api/handovers/${handoverId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const responseClone = response.clone();
      let responseText;
      try {
        responseText = await responseClone.text();
      } catch (e) {
        responseText = await response.text();
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Server returned invalid response');
      }
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send handover');
      }
      
      toast.success(`Handover sent to ${formData.customer_email}`);
      
    } catch (error) {
      console.error('Error sending handover:', error);
      toast.error(error.message || 'Failed to send handover');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="text-[#c9a55a]">Loading...</div>
      </div>
    );
  }

  const balanceDue = (formData.total_cost || 0) - (formData.amount_received || 0);

  return (
    <div className="min-h-screen bg-[#1a1f1a]">
      {/* Header */}
      <header className="bg-[#2d5f3f]/50 border-b border-[#2d5f3f] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                className="text-[#f5f3ed] hover:bg-[#2d5f3f]/30"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(`/admin/bookings/${bookingId}`)}
                className="text-[#f5f3ed] hover:bg-[#2d5f3f]/30"
                title="Back to Booking"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Booking
              </Button>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#c9a55a]">Handover Form</h1>
              <p className="text-[#f5f3ed]/70 text-sm">
                {isNewHandover ? 'Create new handover' : `Handover ID: ${handover?.handover_id}`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSendHandover}
              disabled={sending || saving || !formData.customer_email}
              variant="outline"
              className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
              data-testid="send-handover-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              {sending ? 'Saving & Sending...' : 'Save & Send'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || sending}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
              data-testid="save-handover-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save Handover'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Customer Information */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#c9a55a]" />
              Customer Information
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">First Name</Label>
                <Input
                  name="customer_first_name"
                  value={formData.customer_first_name}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Surname</Label>
                <Input
                  name="customer_surname"
                  value={formData.customer_surname}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#f5f3ed]/70">Address</Label>
                <Input
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Postcode</Label>
                <Input
                  name="customer_postcode"
                  value={formData.customer_postcode}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Telephone</Label>
                <Input
                  name="customer_telephone"
                  value={formData.customer_telephone}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#f5f3ed]/70">Email</Label>
                <Input
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#f5f3ed]/70">DVLA Check Code</Label>
                <Input
                  name="customer_dvla_check_code"
                  value={formData.customer_dvla_check_code}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
            </div>
            
            {/* Bank Details */}
            <h3 className="text-md font-semibold text-[#f5f3ed] mt-6 mb-3">Bank Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-[#f5f3ed]/70">Account Holder Name</Label>
                <Input
                  name="customer_account_holder_name"
                  value={formData.customer_account_holder_name}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Sort Code</Label>
                <Input
                  name="customer_sort_code"
                  value={formData.customer_sort_code}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Account Number</Label>
                <Input
                  name="customer_bank_account_number"
                  value={formData.customer_bank_account_number}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
            </div>
            
            {/* Additional Notes */}
            <h3 className="text-md font-semibold text-[#f5f3ed] mt-6 mb-3">Additional Notes</h3>
            <div>
              <Textarea
                name="customer_additional_notes"
                value={formData.customer_additional_notes}
                onChange={handleInputChange}
                placeholder="Any additional notes about this customer..."
                rows={3}
                className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] resize-none"
                data-testid="handover-customer-additional-notes"
              />
            </div>
            
            {/* Documents */}
            {Object.keys(customerDocuments).length > 0 && (
              <>
                <h3 className="text-md font-semibold text-[#f5f3ed] mt-6 mb-3">Documents on File</h3>
                <div className="space-y-2">
                  {Object.entries(customerDocuments).map(([type, doc]) => {
                    if (!doc || !doc.filename) return null;
                    return (
                      <div key={type} className="flex items-center justify-between bg-[#1a1f1a]/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#c9a55a]" />
                          <span className="text-[#f5f3ed]/80 text-sm">
                            {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          {doc.is_expired && (
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">
                              Expired
                            </span>
                          )}
                        </div>
                        <a
                          href={`${API_URL}/api/customers/${formData.customer_id}/documents/${type}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#c9a55a] hover:underline text-sm flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </a>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Booking Information */}
          <div className="space-y-6">
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-[#c9a55a]" />
                Booking Details
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#f5f3ed]/70">Collection Date</Label>
                  <Input
                    type="date"
                    name="collection_date"
                    value={formData.collection_date}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Collection Time</Label>
                  <Input
                    type="time"
                    name="collection_time"
                    value={formData.collection_time}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Return Date</Label>
                  <Input
                    type="date"
                    name="return_date"
                    value={formData.return_date}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Return Time</Label>
                  <Input
                    type="time"
                    name="return_time"
                    value={formData.return_time}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Total Cost (£)</Label>
                  <Input
                    type="number"
                    name="total_cost"
                    value={formData.total_cost}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Amount Received (£)</Label>
                  <Input
                    type="number"
                    name="amount_received"
                    value={formData.amount_received}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <div className={`p-3 rounded-lg ${balanceDue > 0 ? 'bg-orange-500/20' : 'bg-green-500/20'}`}>
                    <span className={balanceDue > 0 ? 'text-orange-300' : 'text-green-300'}>
                      Balance Due: £{balanceDue.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <Checkbox
                    id="security_deposit_received"
                    checked={formData.security_deposit_received}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, security_deposit_received: checked }))
                    }
                    className="border-[#2d5f3f] data-[state=checked]:bg-[#c9a55a]"
                  />
                  <Label htmlFor="security_deposit_received" className="text-[#f5f3ed]/70 cursor-pointer">
                    Security Deposit Received
                  </Label>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-blue-400" />
                Vehicle Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-[#f5f3ed]/70">Current Mileage</Label>
                  <Input
                    type="number"
                    name="vehicle_mileage"
                    value={formData.vehicle_mileage}
                    onChange={handleInputChange}
                    placeholder="Enter current odometer reading"
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">Vehicle Condition Notes</Label>
                  <Textarea
                    name="vehicle_condition_notes"
                    value={formData.vehicle_condition_notes}
                    onChange={handleInputChange}
                    placeholder="Note any existing damage, scratches, or condition issues..."
                    rows={4}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed] resize-none"
                  />
                  <p className="text-[#f5f3ed]/40 text-xs mt-1">
                    Pre-populated from the last handover
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signatures Section */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Customer Signature */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#c9a55a]" />
              Customer Signature
            </h2>
            
            <p className="text-[#f5f3ed]/70 text-sm mb-4">
              By signing below, I acknowledge that I have read and accept the{' '}
              <a 
                href="/terms-and-conditions.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#c9a55a] hover:underline"
              >
                Terms and Conditions
              </a>.
            </p>
            
            {formData.customer_signature && customerSigned ? (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <img 
                    src={formData.customer_signature} 
                    alt="Customer Signature" 
                    className="max-h-32 mx-auto"
                  />
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>Signed on {formatDate(formData.customer_signature_date)}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => clearSignature(
                    customerCanvasRef, customerCtxRef, setCustomerSigned,
                    'customer_signature', 'customer_signature_date'
                  )}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Signature
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg touch-none">
                  <canvas
                    ref={customerCanvasRef}
                    className="w-full h-40 cursor-crosshair"
                    onMouseDown={(e) => startDrawing(e, customerCanvasRef, customerCtxRef, setIsCustomerDrawing)}
                    onMouseMove={(e) => draw(e, customerCanvasRef, customerCtxRef, isCustomerDrawing)}
                    onMouseUp={() => stopDrawing(customerCtxRef, setIsCustomerDrawing)}
                    onMouseLeave={() => stopDrawing(customerCtxRef, setIsCustomerDrawing)}
                    onTouchStart={(e) => startDrawing(e, customerCanvasRef, customerCtxRef, setIsCustomerDrawing)}
                    onTouchMove={(e) => draw(e, customerCanvasRef, customerCtxRef, isCustomerDrawing)}
                    onTouchEnd={() => stopDrawing(customerCtxRef, setIsCustomerDrawing)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => clearSignature(
                      customerCanvasRef, customerCtxRef, setCustomerSigned,
                      'customer_signature', 'customer_signature_date'
                    )}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() => confirmSignature(
                      customerCanvasRef, setCustomerSigned,
                      'customer_signature', 'customer_signature_date'
                    )}
                    className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
                  >
                    Confirm Signature
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Representative Signature */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-blue-400" />
              Equi-Go Representative Signature
            </h2>
            
            <div className="mb-4">
              <Label className="text-[#f5f3ed]/70">Representative Name</Label>
              <Input
                name="representative_name"
                value={formData.representative_name}
                onChange={handleInputChange}
                placeholder="Enter representative name"
                className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
              />
            </div>
            
            {formData.representative_signature && repSigned ? (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <img 
                    src={formData.representative_signature} 
                    alt="Representative Signature" 
                    className="max-h-32 mx-auto"
                  />
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>Signed on {formatDate(formData.representative_signature_date)}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => clearSignature(
                    repCanvasRef, repCtxRef, setRepSigned,
                    'representative_signature', 'representative_signature_date'
                  )}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Signature
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg touch-none">
                  <canvas
                    ref={repCanvasRef}
                    className="w-full h-40 cursor-crosshair"
                    onMouseDown={(e) => startDrawing(e, repCanvasRef, repCtxRef, setIsRepDrawing)}
                    onMouseMove={(e) => draw(e, repCanvasRef, repCtxRef, isRepDrawing)}
                    onMouseUp={() => stopDrawing(repCtxRef, setIsRepDrawing)}
                    onMouseLeave={() => stopDrawing(repCtxRef, setIsRepDrawing)}
                    onTouchStart={(e) => startDrawing(e, repCanvasRef, repCtxRef, setIsRepDrawing)}
                    onTouchMove={(e) => draw(e, repCanvasRef, repCtxRef, isRepDrawing)}
                    onTouchEnd={() => stopDrawing(repCtxRef, setIsRepDrawing)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => clearSignature(
                      repCanvasRef, repCtxRef, setRepSigned,
                      'representative_signature', 'representative_signature_date'
                    )}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() => confirmSignature(
                      repCanvasRef, setRepSigned,
                      'representative_signature', 'representative_signature_date'
                    )}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Confirm Signature
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HandoverForm;
